import { useApolloClient } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useContext, useMemo, useState } from 'react';
import { View } from '@shared/components/View';
import { SignatureView } from '@shared/components/blocks/lib/inputSignature/signature';
import { SignatureBlockData } from '@shared/components/blocks/lib/inputSignature/signature/SignatureProps';
import { InputUserAndDate } from '@shared/components/blocks/lib/inputUserAndDate';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBDefaultError } from '@shared/components/wrapper';
import { SignatureField } from '@shared/data/entity/fields/SignatureField';
import { EditFieldContainer } from '@shared/scenes/entity/components/fields/EditFieldContainer';
import { uploadFile } from '@shared/scenes/upload/store/actions';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { formatDateTimeWithYear } from '@shared/util/date';
import { useCurrentUserId, useListUsers } from '@shared/util/hooks';
import * as uuid from '@shared/util/uuid';
import { useResponsiveFormContext } from '../ResponsiveFormContext';

interface Props {
  field: SignatureField | null;
  component: API.SignatureComponent;
  updateField: (componentId: string, field: SignatureField | null) => void;
  error?: string;
  style?: CSSProperties;
}

export function SignatureEditField({ field, component, updateField, error, style }: Props) {
  const responsiveForms = useResponsiveFormContext();
  const colors = useContext(ColorThemeContext);
  const height = responsiveForms ? grid(9.5) : grid(10);
  const {
    uploadSignature,
    clearSignature,
    isLoading,
    error: signatureError,
  } = useUploadSignature(component, updateField);

  const signatureUrl = field?.signature.url;
  const userId = field?.signature.userId;
  const date = field?.signature.date;
  const hasSignature = !isNil(signatureUrl);
  const hasError = !isEmpty(signatureError);

  const { users, loading: loadingUsers } = useListUsers({
    input: {
      ids: userId ? [userId] : [],
      excludeSystemUsers: true,
      orderBys: [{ orderBy: 'firstName', direction: 'ASC' }],
    },
    skip: !hasSignature || isNil(userId),
  });
  const user = useMemo(
    () => (!isNil(field?.signature.url) ? users?.find(u => u.id === userId) : undefined),
    [field?.signature.url, users, userId],
  );

  function setDataUrl(url: string) {
    if (isEmpty(url)) {
      clearSignature();
    } else {
      uploadSignature(url, 'signature.png');
    }
  }

  const showUserAndDateInFooter = hasSignature && !loadingUsers && responsiveForms;
  const showUserAndDateBelow = hasSignature && !loadingUsers && !responsiveForms;
  const printContent = hasSignature
    ? [user?.fullName, date ? formatDateTimeWithYear(date) : null].filter(Boolean).join('\n') ||
      undefined
    : undefined;

  return (
    <EditFieldContainer
      component={component}
      showLabel={true}
      indentLabel={true}
      showIcon={false}
      error={error}
      largerInput={false}
      footer={
        showUserAndDateInFooter && <InputUserAndDate user={user} date={date} indent={false} />
      }
      style={style}
      printContent={printContent}
    >
      {(isLoading || hasError) && (
        <View
          flex
          center
          style={{
            borderRadius: grid(1),
            borderStyle: 'dashed',
            borderWidth: responsiveForms ? 0 : 2,
            height,
            flexBasis: height,
            backgroundColor: responsiveForms
              ? Colors.v2.transparent
              : colors.input.backgroundInactive,
            borderColor: isEmpty(signatureUrl)
              ? colors.input.borderInactive
              : colors.input.borderActive,
          }}
        >
          {isLoading && <MBLoadingIndicator />}
          {hasError && <MBDefaultError message={signatureError} />}
        </View>
      )}
      {!isLoading && (
        <>
          <SignatureView
            onSignatureUpdate={url => setDataUrl(url)}
            height={height}
            block={component as SignatureBlockData}
            signatureUrl={signatureUrl}
          />
          {showUserAndDateBelow && <InputUserAndDate user={user} date={date} indent={true} />}
        </>
      )}
    </EditFieldContainer>
  );
}

interface SignatureEditFieldHooks {
  uploadSignature(data: string, filename: string): Promise<void>;
  clearSignature(): void;
  isLoading: boolean;
  error?: string;
}

function useUploadSignature(
  component: API.SignatureComponent,
  updateField: (componentId: string, field: SignatureField | null) => void,
): SignatureEditFieldHooks {
  const currentUserId = useCurrentUserId();
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const upload = useCallback(
    async (url: string, name: string): Promise<string> => {
      const fileId = uuid.v4();
      const file = {
        id: fileId,
        url,
        name,
        contentType: 'image/png',
      };
      return uploadFile(client, file);
    },
    [client],
  );

  const uploadSignature = useCallback(
    async (uri: string, fileName: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const url = await upload(uri, fileName);

        updateField(
          component.id,
          new SignatureField({
            url,
            name: fileName,
            contentType: 'image/png',
            userId: currentUserId,
            date: new Date().toISOString(),
          }),
        );
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [component.id, updateField, upload, currentUserId],
  );

  const clearSignature = () => {
    updateField(component.id, null);
    setError(null);
  };

  return {
    uploadSignature,
    clearSignature,
    isLoading,
    error,
  };
}
